// The head of every public page: title, description, canonical, Open Graph,
// Twitter card, and structured data. Reads the current route from the router
// and the copy from pages.js, so a page gets its metadata by being listed.
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router';
import { PAGES, SITE } from './pages';
import landingData from '../screens/app/landingScreen/data/landingData.json';

const organization = {
  '@type': 'Organization',
  '@id': `${SITE.url}/#organization`,
  name: SITE.name,
  url: SITE.url,
  logo: `${SITE.url}/images/flux_uro_logo_with_text_white_inline.png`,
  sameAs: [],
};

const website = {
  '@type': 'WebSite',
  '@id': `${SITE.url}/#website`,
  url: SITE.url,
  name: SITE.name,
  publisher: { '@id': `${SITE.url}/#organization` },
};

const application = {
  '@type': 'SoftwareApplication',
  name: 'Flux URO',
  applicationCategory: 'MultimediaApplication',
  operatingSystem: 'Web',
  url: SITE.url,
  description: PAGES['/'].description,
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD', description: 'Pay per generation; costs shown before every run.' },
  featureList: ['Text to image', 'Image editing', 'Image to video', 'Text to video', 'Music and song generation', 'Text to speech', 'API and MCP access'],
};

const faqPage = {
  '@type': 'FAQPage',
  mainEntity: (landingData.faqs || []).map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: { '@type': 'Answer', text: f.answer },
  })),
};

const breadcrumbs = (path, title) => ({
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: SITE.url },
    { '@type': 'ListItem', position: 2, name: title, item: `${SITE.url}${path}` },
  ],
});

function graphFor(path, page) {
  const nodes = [organization, website];
  if (path === '/') nodes.push(application, faqPage);
  else nodes.push(breadcrumbs(path, page.title));
  if (path === '/faq') nodes.push(faqPage);
  if (path === '/mcp' || path === '/developers') {
    nodes.push({ '@type': 'SoftwareApplication', name: path === '/mcp' ? 'Flux URO for ChatGPT and Claude' : 'Flux URO API',
      applicationCategory: 'DeveloperApplication', operatingSystem: 'Web', url: `${SITE.url}${path}`, description: page.description,
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' } });
  }
  return { '@context': 'https://schema.org', '@graph': nodes };
}

export default function Seo() {
  const { pathname } = useLocation();
  const path = pathname.replace(/\/+$/, '') || '/';
  const page = PAGES[path];
  const canonical = `${SITE.url}${path === '/' ? '/' : path}`;
  const title = page ? page.title : `${SITE.name}`;
  const description = page ? page.description : PAGES['/'].description;
  const robots = page && page.noindex ? 'noindex,follow' : 'index,follow,max-image-preview:large';
  return (
    <Helmet>
      <html lang="en" />
      <title>{title}</title>
      <meta name="description" content={description} />
      {page && page.keywords ? <meta name="keywords" content={page.keywords} /> : null}
      <meta name="robots" content={page ? robots : 'noindex,follow'} />
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={SITE.ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={SITE.twitter} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={SITE.ogImage} />
      {page ? <script type="application/ld+json">{JSON.stringify(graphFor(path, page))}</script> : null}
    </Helmet>
  );
}
